import {
    Box,
    Chip,
    FloatingPanel,
    FloatingPanelBody,
    FloatingPanelContent,
    FloatingPanelFooter,
    FloatingPanelHeader,
    Heading,
    IconButton,
    LogoLoader,
    Maximize,
    Minimize,
    showToast,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useFloatingPanelContext,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import remarkGfm from "remark-gfm";
import type { AiToolsApi, TabContext, WorkflowCheckerIssue } from "workflowEditorV3/WorkflowEditorV3";

import { AISupportCard } from "./ai-cards/ai-support-card/AISupportCard";
import { AIGettingStartedPanel } from "./getting-started/AIGettingStartedPanel";
import { renderResolvedProductCard } from "components/ai-panel/ai-cards/ai-product-card/AIProductCard";
import AIDisclaimer from "components/ai-panel/ai-disclaimer/AIDisclaimer";
import { ConversationManagement } from "components/ai-panel/ai-menu/AIMenu";
import { AiPanelButton } from "components/ai-panel/ai-panel-button/AiPanelButton";
import style from "components/ai-panel/ai-panel.scss";
import { AIQueryInput } from "components/ai-panel/ai-query-input/AIQueryInput";
import { ConversationManagementHandlers, useAIPanelContext } from "components/ai-panel/AIPanelContext";
import { CodeBlock } from "components/ai-panel/code-block/CodeBlock";
import { AIFeedbackModal } from "components/ai-panel/feedback/AIFeedbackModal";
import {
    extractWorkflowBlocks,
    getSafeMarkdown,
    splitFollowUpPrompts,
    streamTagContent,
    transformContent,
} from "components/ai-panel/markdownStreamingUtils";
import {
    extractProductTags,
    findFirstProductTagIndex,
    ResolvedProduct,
    resolveProducts,
} from "components/ai-panel/productTagUtils";
import { Products, useFilteredProducts } from "components/ai-panel/useFilteredProducts";
import { FeatureBadge } from "components/feature-badge/FeatureBadge";
import HamburgerMenuIcon from "components/icons/HamburgerMenuIcon";
import { IntegrationsIcon } from "components/icons/IntegrationsIcon";
import ThumbsDown from "components/icons/ThumbsDown";
import ThumbsUp from "components/icons/ThumbsUp";
import { Logo } from "components/logo/Logo";
import { HOME_ROUTE } from "components/router/Routes";
import WorkflowEditorV3View from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import { OnboardingState } from "domain/onboardings";
import { WorkflowEntityType } from "domain/workflows";
import { aiAgentService, Session } from "services/ai-agent/AiAgentService";
import {
    FLAG_AI_LICENSE_CONSUMPTION,
    FLAG_AI_ONBOARDING,
    FLAG_RESTRUCTURED_NAVIGATION,
} from "services/feature/FeatureFlagService";
import * as LanguageRepository from "services/language/languageRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setUser } from "store/user";
import { isStringNotBlank, trimToNull } from "utils/commonFunctions";
import { getLocalTimezone } from "utils/timezones";
import { useIsDarkThemeActive } from "utils/useTheme";

import testIds from "testIds.json";

const EDITOR_GENERATION_V3 = "V3";

export type AIPanelLayout = "floating" | "page" | "subMenu";

interface Props {
    layout: AIPanelLayout;
}

type MessageRole = "user" | "assistant";
type UserFeedback = "positive" | "negative";

interface Message {
    role: MessageRole;
    content: string;
}

const concatAssistantMessages = (messages: Message[]) => {
    return messages.reduce<Message[]>((acc, current) => {
        const prev = acc[acc.length - 1];

        if (prev?.role === "assistant" && current.role === "assistant") {
            acc[acc.length - 1] = { ...prev, content: prev.content.concat("\n\n", current.content) };
        } else {
            acc.push(current);
        }

        return acc;
    }, []);
};

const getFirstName = (fullName: string | undefined): string | undefined => trimToNull(fullName)?.split(/\s+/)[0];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const getStringField = (record: Record<string, unknown> | undefined, field: string): string | undefined =>
    typeof record?.[field] === "string" ? record[field] : undefined;

const getEditorMetadata = (workflow: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
    const editorMetadata = workflow?.editorMetadata;
    return isRecord(editorMetadata) ? editorMetadata : undefined;
};

const getWorkflowName = (workflow: Record<string, unknown> | undefined): string | undefined =>
    getStringField(workflow, "name");

const getWorkflowProfile = (workflow: Record<string, unknown> | undefined): string | undefined =>
    getStringField(getEditorMetadata(workflow), "profile");

const getEditorVersion = (workflow: Record<string, unknown> | undefined): string | undefined =>
    getStringField(getEditorMetadata(workflow), "editorVersion");

const isEditorGenerationV3 = (workflow: Record<string, unknown> | undefined): boolean =>
    getStringField(getEditorMetadata(workflow), "editorGeneration") === EDITOR_GENERATION_V3;

const findTabIndex = (aiToolsApi: AiToolsApi, name: string, product: string, version: string): number | undefined => {
    const index = aiToolsApi
        .getTabs()
        .findIndex((tab) => tab.name === name && tab.product === product && tab.version === version);
    return index === -1 ? undefined : index;
};

const setAiEditModeIfTabOpen = (aiToolsApi: AiToolsApi | undefined, enabled: boolean): void => {
    if (aiToolsApi === undefined || aiToolsApi.getTabs().length === 0) {
        return;
    }
    aiToolsApi.setAiEditMode(enabled);
};

const getCurrentTabIfOpen = async (aiToolsApi: AiToolsApi): Promise<TabContext | undefined> =>
    aiToolsApi.getTabs().length === 0 ? undefined : aiToolsApi.getCurrentTab();

const applyWorkflowBlocks = async (
    aiToolsApi: AiToolsApi,
    accumulated: string
): Promise<WorkflowCheckerIssue[] | undefined> => {
    const workflowBlocks = extractWorkflowBlocks(accumulated);
    if (workflowBlocks.length === 0) {
        return undefined;
    }

    let latestErrors: WorkflowCheckerIssue[] | undefined;
    for (const block of workflowBlocks) {
        const workflowName = getWorkflowName(block.json);
        const currentTabWorkflowName = getWorkflowName((await getCurrentTabIfOpen(aiToolsApi))?.workflow);
        if (workflowName !== currentTabWorkflowName) {
            if (!isEditorGenerationV3(block.json)) {
                continue;
            }
            const workflowProfile = getWorkflowProfile(block.json);
            const editorVersion = getEditorVersion(block.json);
            if (workflowName === undefined || workflowProfile === undefined || editorVersion === undefined) {
                continue;
            }
            const existingTabIndex = findTabIndex(aiToolsApi, workflowName, workflowProfile, editorVersion);
            if (existingTabIndex !== undefined) {
                const switchTabResult = aiToolsApi.switchTab(existingTabIndex);
                if (switchTabResult.status !== "success") {
                    continue;
                }
            } else {
                const addTabResult = await aiToolsApi.addTab(workflowName, workflowProfile, editorVersion);
                if (addTabResult.status !== "success") {
                    continue;
                }
            }
        }
        const result = await aiToolsApi.setCurrentTabWorkflow(block.json);
        if (result.status === "success") {
            latestErrors = result.errors;
        }
    }
    return latestErrors;
};

export const MAX_AUTOMATIC_MESSAGES = 3;
// When the agent creates subworkflows it can't save them, so this error appears, and it can't fix it itself so need to ignore it
export const WORKFLOW_NOT_FOUND_ERROR_MESSAGE_PATTERN = /^Workflow ".+" was not found\. It may have been removed$/;

const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
    MESSAGE_REQUIRED: "AiAgent.errors.messageRequired",
    IDENTITY_REQUIRED: "AiAgent.errors.identityRequired",
    TURN_ALREADY_IN_PROGRESS: "AiAgent.errors.turnAlreadyInProgress",
    NOT_ENOUGH_LICENSES: "AiAgent.errors.notEnoughLicenses",
    LICENSE_CONSUMPTION_FAILED: "AiAgent.errors.licenseConsumptionFailed",
    MODEL_INVOCATION_FAILED: "AiAgent.errors.modelInvocationFailed",
    TURN_LIMIT_REACHED: "AiAgent.errors.turnLimitReached",
};

const getErrorTranslationKey = (code: string): string => ERROR_CODE_TRANSLATION_KEYS[code] ?? "AiAgent.errorResponse";

const TOOL_STATUS_TRANSLATION_KEYS: Record<string, string> = {
    getCurrentView: "AiAgent.toolStatus.getCurrentView",
    getLocale: "AiAgent.toolStatus.getLocale",
    getUserAndTenantInfo: "AiAgent.toolStatus.getUserAndTenantInfo",
    saveUserProfile: "AiAgent.toolStatus.saveUserProfile",
    getAvailableLicenses: "AiAgent.toolStatus.getAvailableLicenses",
    getCommonLicenseRates: "AiAgent.toolStatus.getCommonLicenseRates",
    getReport: "AiAgent.toolStatus.getReport",
    countReports: "AiAgent.toolStatus.countReports",
    searchReports: "AiAgent.toolStatus.searchReports",
    searchWorkflows: "AiAgent.toolStatus.searchWorkflows",
    getWorkflowSchema: "AiAgent.toolStatus.getWorkflowSchema",
    getCurrentWorkflow: "AiAgent.toolStatus.getCurrentWorkflow",
    getWorkflowErrors: "AiAgent.toolStatus.getWorkflowErrors",
    getUserProfileGuide: "AiAgent.toolStatus.getUserProfileGuide",
    searchProductManuals: "AiAgent.toolStatus.searchProductManuals",
    getReportProductNames: "AiAgent.toolStatus.getReportProductNames",
    searchReportProductIds: "AiAgent.toolStatus.searchReportProductIds",
    searchReportFieldPaths: "AiAgent.toolStatus.searchReportFieldPaths",
    createWorkflow: "AiAgent.toolStatus.createWorkflow",
    editWorkflow: "AiAgent.toolStatus.editWorkflow",
};

const getToolStatus = (toolName: string, translate: (key: string) => string): string | undefined => {
    const translationKey = TOOL_STATUS_TRANSLATION_KEYS[toolName];
    return translationKey ? translate(translationKey) : undefined;
};

const getAutoFixableErrors = (errors: WorkflowCheckerIssue[] | undefined): WorkflowCheckerIssue[] =>
    (errors ?? []).filter(
        (error) => error.severity === "error" && !WORKFLOW_NOT_FOUND_ERROR_MESSAGE_PATTERN.test(error.message)
    );

const SUGGESTION_REVEAL_INTERVAL_MILLISECONDS = 150;
const REVEAL_INTERVAL_MILLISECONDS = 30;
const MAXIMIZE_BREAKPOINT_PIXELS = 1_100;

interface MarkdownMessageProps {
    message: Message;
    isStreaming: boolean;
    isActivelyStreaming: boolean;
    products: Products | undefined;
    isProductsLoading: boolean;
    onOnboardingStateChange: (updater: (prev: OnboardingState) => OnboardingState) => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;
}

const MarkdownMessage = (props: MarkdownMessageProps) => {
    const isStreamingRef = React.useRef(props.isStreaming);
    isStreamingRef.current = props.isStreaming;

    const productsRef = React.useRef(props.products);
    productsRef.current = props.products;

    const onOnboardingStateChangeRef = React.useRef(props.onOnboardingStateChange);
    onOnboardingStateChangeRef.current = props.onOnboardingStateChange;

    const resolvedProducts = props.products
        ? resolveProducts(extractProductTags(props.message.content), props.products.softwarePackages)
        : new Map<string, ResolvedProduct>();
    const resolvedProductsRef = React.useRef(resolvedProducts);
    resolvedProductsRef.current = resolvedProducts;

    const markdownComponentsRef = React.useRef({
        code({ children, className }: { children?: React.ReactNode; className?: string }) {
            const languageMatch = /language-([\w-]+)/.exec(className ?? "");
            if (languageMatch) {
                const isWorkflow = languageMatch[1].includes("workflow");
                const language = isWorkflow ? languageMatch[1].replace(/-?workflow-?/g, "") : languageMatch[1];
                return (
                    language && (
                        <CodeBlock
                            content={String(children)}
                            language={language}
                            isLoading={isStreamingRef.current}
                            isWorkflow={isWorkflow}
                        />
                    )
                );
            }
            const productMatch = /^product:([^:]+):(.*)$/.exec(String(children));
            if (productMatch) {
                const productId = productMatch[1];
                const productKey = productMatch[2];

                const key = `${productId}:${productKey}`;
                const products = productsRef.current;
                const resolved = resolvedProductsRef.current.get(key);
                if (!products) {
                    return null;
                }
                return renderResolvedProductCard(key, resolved, products, onOnboardingStateChangeRef.current);
            }
            const toolMatch = /^tool:(.*)$/.exec(String(children));
            if (toolMatch) {
                return (
                    <Box gap={"Xxs"} verticalAlignment={"Center"}>
                        <IntegrationsIcon color={"var(--theme-icon-fill-default)"} />
                        {toolMatch[1]}
                    </Box>
                );
            }
            const supportMatch = /^support$/.exec(String(children));
            if (supportMatch) {
                return <AISupportCard />;
            }
            const supportSummarizationMatch = /^summary$/.exec(String(children));
            if (supportSummarizationMatch) {
                return;
            }

            return children;
        },
        table({ children }: { children?: React.ReactNode }) {
            return (
                <div className={style.tableWrapper}>
                    <table>{children}</table>
                </div>
            );
        },
        ul({ children }: { children: React.ReactNode }) {
            return <ul className={classNames(style.markdownList, style.markdownUnorderedList)}>{children}</ul>;
        },
        ol({ children }: { children: React.ReactNode }) {
            return <ol className={classNames(style.markdownList, style.markdownOrderedList)}>{children}</ol>;
        },
    });

    const safeContent = props.isActivelyStreaming ? getSafeMarkdown(props.message.content) : props.message.content;

    const firstProductTagIndex = findFirstProductTagIndex(safeContent);
    const isWaitingForProducts = firstProductTagIndex !== -1 && !props.products && props.isProductsLoading;
    const fullContent = isWaitingForProducts ? safeContent.slice(0, firstProductTagIndex) : safeContent;

    // Reveal already-buffered content at a steady pace instead of snapping to it whenever a
    // chunk arrives. This absorbs uneven network arrival (a pause for a tool call, followed by
    // several chunks arriving close together) so the text keeps moving smoothly instead of
    // alternating between stalls and jumps. Resets naturally per message since each streamed
    // message is a fresh component instance.
    const [revealedLength, setRevealedLength] = React.useState(0);

    React.useEffect(() => {
        if (!props.isActivelyStreaming || revealedLength >= fullContent.length) {
            return;
        }
        const timeout = setTimeout(() => {
            setRevealedLength((prev) => {
                const nextSpace = fullContent.indexOf(" ", prev);
                const nextNewline = fullContent.indexOf("\n", prev);
                const candidates = [nextSpace, nextNewline].filter((i) => i !== -1);
                return candidates.length === 0 ? fullContent.length : Math.min(...candidates) + 1;
            });
        }, REVEAL_INTERVAL_MILLISECONDS);
        return () => clearTimeout(timeout);
    }, [revealedLength, fullContent, props.isActivelyStreaming]);

    React.useEffect(() => {
        if (props.isActivelyStreaming) {
            props.messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
        }
    }, [revealedLength, props.isActivelyStreaming]);

    // getSafeMarkdown runs again on the revealed slice: our own word-boundary cut can land
    // mid-span (e.g. an unclosed bold marker) just like a network-arrival cut can.
    const content = props.isActivelyStreaming ? getSafeMarkdown(fullContent.slice(0, revealedLength)) : fullContent;

    return (
        <div className={style.markdown} data-testid={testIds.workArea.aiPanel.message}>
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponentsRef.current}>
                {content}
            </Markdown>
            {isWaitingForProducts && <LogoLoader />}
        </div>
    );
};

const MaximizeButton = () => {
    const { isMaximized, setIsMaximized } = useFloatingPanelContext();
    const handleCLick = () => {
        usageStatisticsService.sendEvent({
            category: Category.AI_AGENT,
            action: isMaximized ? Action.MINIMIZE_PANEL : Action.MAXIMIZE_PANEL,
        });
        setIsMaximized((prev) => !prev);
    };
    return (
        <IconButton
            size="Sm"
            onClick={() => handleCLick()}
            className={classNames(style.iconButton, style.resizeButton)}
        >
            {!isMaximized ? (
                <Maximize color={"--app-aiPanel-iconColor"} />
            ) : (
                <Minimize color={"--app-aiPanel-iconColor"} />
            )}
        </IconButton>
    );
};

interface AIPanelWelcomeProps {
    showInlineInput: boolean;
    onSubmit: (message: string) => void;
    isSubmitting: boolean;
    disabled: boolean;
    placeholder: string;
    isPageLayout: boolean;
    fullWidthOnDesktop: boolean;
}

const AIPanelWelcome = (props: AIPanelWelcomeProps) => {
    const { t } = useTranslation();

    return (
        <Box columnLayout verticalAlignment="Center" gap="Xs">
            <Heading variant="h1" className={style.heading}>
                {t("AiAgent.greeting")}
            </Heading>
            {props.showInlineInput && (
                <AIQueryInput
                    onSubmit={props.onSubmit}
                    disabled={props.disabled}
                    isSubmitting={props.isSubmitting}
                    placeholder={props.placeholder}
                    queryInputTestId={testIds.workArea.aiPanel.queryInput}
                    submitButtonTestId={testIds.workArea.aiPanel.submitButton}
                    hideDisclaimer
                    fullWidthOnDesktop={props.fullWidthOnDesktop}
                />
            )}

            <Box marginTop={props.isPageLayout ? "Xs" : "None"}>
                <AIGettingStartedPanel
                    onSubmit={(query: string) => {
                        props.onSubmit(query);
                    }}
                />
            </Box>
            {props.showInlineInput && (
                <Box className={style.footerDisclaimer}>
                    <AIDisclaimer />
                </Box>
            )}
        </Box>
    );
};

interface AIPanelOnboardingWelcomeProps {
    firstName: string | undefined;
    isDarkMode: boolean;
    products: Products | undefined;
    isProductsLoading: boolean;
    onOnboardingStateChange: (updater: (prev: OnboardingState) => OnboardingState) => void;
    onAccept: () => void;
    onDecline: () => void;
}

const AIPanelOnboardingWelcome = (props: AIPanelOnboardingWelcomeProps) => {
    const { t } = useTranslation();
    const responseOptions = [
        { label: t("Common.yes"), handleClick: props.onAccept },
        { label: t("Common.no"), handleClick: props.onDecline },
    ];
    const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
    const message: Message = {
        role: "assistant",
        content: t("AiAgent.onboarding.greeting", { firstName: props.firstName }),
    };

    return (
        <Box columnLayout className={style.messages}>
            <Box columnLayout gap="Md">
                <Box columnLayout gap="Xs">
                    <div className={classNames(style.messageBlob, style.assistantBlob)}>
                        <Box gap="Xxs">
                            <Logo hiddenText isWhiteLogo={props.isDarkMode} />
                            <MarkdownMessage
                                message={message}
                                isStreaming={false}
                                isActivelyStreaming={false}
                                products={props.products}
                                isProductsLoading={props.isProductsLoading}
                                onOnboardingStateChange={props.onOnboardingStateChange}
                                messagesEndRef={messagesEndRef}
                            />
                        </Box>
                    </div>
                </Box>
                <div className={classNames(style.suggestions, style.assistantIndent)}>
                    {responseOptions.map((option) => (
                        <Chip
                            key={option.label}
                            handleClick={option.handleClick}
                            testId={testIds.workArea.aiPanel.suggestion}
                            className={style.suggestionChip}
                        >
                            {option.label}
                        </Chip>
                    ))}
                </div>
            </Box>
        </Box>
    );
};

interface AIPanelMessagesProps {
    messages: Message[];
    isLoading: boolean;
    isStreaming: boolean;
    toolStatus: string | undefined;
    suggestions: string[];
    visibleSuggestionCount: number;
    feedbackAnswer: UserFeedback | undefined;
    isDarkMode: boolean;
    onSuggestionClick: (suggestion: string) => void;
    onFeedbackClick: (rating: UserFeedback) => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    attachMessagesEndRef: (node: HTMLDivElement | null) => void;
    products: Products | undefined;
    isProductsLoading: boolean;
    onOnboardingStateChange: (updater: (prev: OnboardingState) => OnboardingState) => void;
}

const AIPanelMessages = (props: AIPanelMessagesProps) => {
    const { t } = useTranslation();

    return (
        <Box columnLayout className={style.messages}>
            <Box columnLayout gap="Md">
                {props.messages.map((message, index) => (
                    <Box key={index} columnLayout gap="Xs">
                        <div
                            className={classNames(style.messageBlob, {
                                [style.userBlob]: message.role === "user",
                                [style.assistantBlob]: message.role === "assistant",
                            })}
                        >
                            <Box gap="Xxs">
                                {message.role === "assistant" && <Logo hiddenText isWhiteLogo={props.isDarkMode} />}
                                {message.role === "assistant" ? (
                                    <MarkdownMessage
                                        message={message}
                                        isStreaming={props.isStreaming}
                                        isActivelyStreaming={props.isStreaming && index === props.messages.length - 1}
                                        products={props.products}
                                        isProductsLoading={props.isProductsLoading}
                                        onOnboardingStateChange={props.onOnboardingStateChange}
                                        messagesEndRef={props.messagesEndRef}
                                    />
                                ) : (
                                    message.content
                                )}
                            </Box>
                        </div>
                    </Box>
                ))}
                {props.isLoading && (
                    <div
                        data-testid={testIds.workArea.aiPanel.loadingIndicator}
                        className={classNames(style.messageBlob, style.assistantBlob)}
                    >
                        <Box gap="Xxs">
                            <Logo hiddenText isWhiteLogo={props.isDarkMode} />

                            {props.toolStatus ? (
                                <Box gap="Xxs">
                                    <IntegrationsIcon color={"var(--theme-icon-fill-default)"} isAnimated />
                                    <span className={style.toolStatusShimmer}>
                                        {props.toolStatus}
                                        <span className={style.ellipsis} aria-hidden="true">
                                            <span>.</span>
                                            <span>.</span>
                                            <span>.</span>
                                        </span>
                                    </span>
                                </Box>
                            ) : (
                                <div className={style.dots}>
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            )}
                        </Box>
                    </div>
                )}
                {props.suggestions.length > 0 && !props.isLoading && props.visibleSuggestionCount > 0 && (
                    <div className={classNames(style.suggestions, style.assistantIndent)}>
                        {props.suggestions.slice(0, props.visibleSuggestionCount).map((suggestion, index) => (
                            <Chip
                                key={index}
                                handleClick={() => props.onSuggestionClick(suggestion)}
                                testId={testIds.workArea.aiPanel.suggestion}
                                className={style.suggestionChip}
                            >
                                {suggestion}
                            </Chip>
                        ))}
                    </div>
                )}
            </Box>
            {!props.isLoading && !props.isStreaming && props.messages.at(-1)?.role === "assistant" && (
                <Box gap="Xs" className={style.assistantIndent}>
                    {props.feedbackAnswer !== "negative" && (
                        <Tooltip placement={"bottom"}>
                            <TooltipTrigger>
                                <IconButton
                                    testId={testIds.workArea.aiPanel.feedbackThumbsUp}
                                    onClick={() => props.onFeedbackClick("positive")}
                                    className={style.feedbackButton}
                                >
                                    <ThumbsUp
                                        color={"var(--app-aiPanel-iconColor)"}
                                        isActive={props.feedbackAnswer === "positive"}
                                    />
                                </IconButton>
                            </TooltipTrigger>
                            <TooltipContent>{t("AiAgent.feedback.goodAnswer")}</TooltipContent>
                        </Tooltip>
                    )}
                    {props.feedbackAnswer !== "positive" && (
                        <Tooltip placement={"bottom"}>
                            <TooltipTrigger>
                                <IconButton
                                    testId={testIds.workArea.aiPanel.feedbackThumbsDown}
                                    onClick={() => props.onFeedbackClick("negative")}
                                    className={style.feedbackButton}
                                >
                                    <ThumbsDown
                                        color={"var(--app-aiPanel-iconColor)"}
                                        isActive={props.feedbackAnswer === "negative"}
                                    />
                                </IconButton>
                            </TooltipTrigger>
                            <TooltipContent>{t("AiAgent.feedback.badAnswer")}</TooltipContent>
                        </Tooltip>
                    )}
                </Box>
            )}
            <div ref={props.attachMessagesEndRef} />
        </Box>
    );
};

interface AIPanelLayoutProps {
    layout: AIPanelLayout;
    sessions: Session[];
    messages: Message[];
    suggestions: string[];
    visibleSuggestionCount: number;
    isLoading: boolean;
    isStreaming: boolean;
    toolStatus: string | undefined;
    isLoadingHistory: boolean;
    feedbackAnswer: UserFeedback | undefined;
    isWelcome: boolean;
    isDarkMode: boolean;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    attachMessagesEndRef: (node: HTMLDivElement | null) => void;
    queryPlaceholder: string;
    onSubmit: (message: string) => void;
    onSuggestionClick: (suggestion: string) => void;
    onFeedbackClick: (rating: UserFeedback) => void;
    onNewConversation: () => void;
    onLoadConversation: (sessionId: string) => void;
    onSessionDeleted: (sessionId: string) => void;
    onSessionRenamed: (sessionId: string, name: string) => void;
    feedbackModal: React.ReactNode;
    products: Products | undefined;
    isProductsLoading: boolean;
    onOnboardingStateChange: (updater: (prev: OnboardingState) => OnboardingState) => void;
    isShowingOnboardingPrompt: boolean;
    firstName: string | undefined;
    onOnboardingAccept: () => void;
    onOnboardingDecline: () => void;
    consumedLicenseCount: number;
}

const FloatingLayout = (props: AIPanelLayoutProps) => {
    const { isAiPanelOpen, setIsAiPanelOpen, setSessions, activeSessionId } = useAIPanelContext();
    const isLicenseConsumptionFeatureEnabled = Boolean(useFeature(FLAG_AI_LICENSE_CONSUMPTION));
    const [isMaximized, setIsMaximized] = React.useState(false);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const hasTrackedInitialOpenState = React.useRef(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    React.useEffect(() => {
        return () => abortControllers.forEach((abortController) => abortController.abort());
    }, []);

    React.useEffect(() => {
        if (!hasTrackedInitialOpenState.current) {
            hasTrackedInitialOpenState.current = true;
            return;
        }
        usageStatisticsService.sendEvent({
            category: Category.AI_AGENT,
            action: isAiPanelOpen ? Action.OPEN_PANEL : Action.CLOSE_PANEL,
        });
    }, [isAiPanelOpen]);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${MAXIMIZE_BREAKPOINT_PIXELS}px)`);
        if (mediaQuery.matches) {
            setIsMaximized(false);
        }
        const handleChange = (event: MediaQueryListEvent) => {
            if (event.matches) {
                setIsMaximized(false);
            }
        };
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    const refreshSessions = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        try {
            setSessions(await aiAgentService.fetchSessions(abortController));
        } catch {
            // Keep whatever sessions are already in state.
        }
    };

    const handleNewConversation = () => {
        setIsMenuOpen(false);
        props.onNewConversation();
    };

    const handleLoadConversation = (sessionId: string) => {
        setIsMenuOpen(false);
        props.onLoadConversation(sessionId);
    };

    return (
        <FloatingPanel isOpen={isAiPanelOpen} onOpenChange={setIsAiPanelOpen}>
            <AiPanelButton isLoading={!isAiPanelOpen && (props.isStreaming || props.isLoading)} />
            <FloatingPanelBody
                className={classNames(style.aiPanel, { ["twoColumnLayout"]: isMaximized })}
                isMaximized={isMaximized}
                onMaximizedChange={setIsMaximized}
                testId={testIds.workArea.aiPanel.floatingPanelContainer}
            >
                <FloatingPanelHeader className={classNames(style.header, { [style.menuOpen]: isMenuOpen })}>
                    <Box
                        justifyContent={isMaximized ? undefined : "SpaceBetween"}
                        horizontalAlignment="Right"
                        marginRight="Xxs"
                    >
                        {!isMaximized && (
                            <Box gap="Sm" verticalAlignment="Center">
                                <IconButton
                                    onClick={() => {
                                        if (!isMenuOpen) {
                                            refreshSessions();
                                        }
                                        setIsMenuOpen(!isMenuOpen);
                                    }}
                                    testId={testIds.workArea.aiPanel.menuButton}
                                    aria-expanded={isMenuOpen}
                                    className={style.iconButton}
                                >
                                    {<HamburgerMenuIcon isOpen={isMenuOpen} color={"--app-aiPanel-iconColor"} />}
                                </IconButton>

                                <FeatureBadge variant="New" isSmall />
                            </Box>
                        )}

                        {!isMenuOpen && <MaximizeButton />}
                    </Box>
                </FloatingPanelHeader>
                <FloatingPanelContent
                    className={classNames(style.body, {
                        [style.centeredContent]: !isMenuOpen && (props.isWelcome || props.isLoadingHistory),
                    })}
                >
                    {!isMaximized && isMenuOpen ? (
                        <ConversationManagement
                            sessions={props.sessions}
                            disabled={props.isLoading || props.isStreaming || props.isLoadingHistory}
                            activeSessionId={activeSessionId}
                            isMaximized={false}
                            layout={props.layout}
                            onNewConversation={handleNewConversation}
                            onLoadConversation={handleLoadConversation}
                            onSessionDeleted={props.onSessionDeleted}
                            onSessionRenamed={props.onSessionRenamed}
                        />
                    ) : (
                        <>
                            {props.isLoadingHistory ? (
                                <Box data-testid={testIds.workArea.aiPanel.historyLoadingIndicator}>
                                    <LogoLoader />
                                </Box>
                            ) : props.isShowingOnboardingPrompt ? (
                                <AIPanelOnboardingWelcome
                                    firstName={props.firstName}
                                    isDarkMode={props.isDarkMode}
                                    products={props.products}
                                    isProductsLoading={props.isProductsLoading}
                                    onOnboardingStateChange={props.onOnboardingStateChange}
                                    onAccept={props.onOnboardingAccept}
                                    onDecline={props.onOnboardingDecline}
                                />
                            ) : props.isWelcome ? (
                                <AIPanelWelcome
                                    showInlineInput={isMaximized}
                                    onSubmit={props.onSubmit}
                                    isSubmitting={props.isLoading}
                                    disabled={props.isLoadingHistory}
                                    placeholder={props.queryPlaceholder}
                                    isPageLayout={false}
                                    fullWidthOnDesktop={isMaximized}
                                />
                            ) : (
                                <AIPanelMessages
                                    messages={props.messages}
                                    isLoading={props.isLoading}
                                    isStreaming={props.isStreaming}
                                    toolStatus={props.toolStatus}
                                    suggestions={props.suggestions}
                                    visibleSuggestionCount={props.visibleSuggestionCount}
                                    feedbackAnswer={props.feedbackAnswer}
                                    isDarkMode={props.isDarkMode}
                                    onSuggestionClick={props.onSuggestionClick}
                                    onFeedbackClick={props.onFeedbackClick}
                                    messagesEndRef={props.messagesEndRef}
                                    attachMessagesEndRef={props.attachMessagesEndRef}
                                    products={props.products}
                                    isProductsLoading={props.isProductsLoading}
                                    onOnboardingStateChange={props.onOnboardingStateChange}
                                />
                            )}
                        </>
                    )}
                </FloatingPanelContent>
                {!isMenuOpen && !props.isShowingOnboardingPrompt && (isMaximized ? !props.isWelcome : true) && (
                    <FloatingPanelFooter className={style.footer}>
                        <AIQueryInput
                            onSubmit={props.onSubmit}
                            disabled={props.isLoadingHistory}
                            isSubmitting={props.isLoading || props.isStreaming}
                            placeholder={props.queryPlaceholder}
                            queryInputTestId={testIds.workArea.aiPanel.queryInput}
                            submitButtonTestId={testIds.workArea.aiPanel.submitButton}
                            hideLicenseConsumptionCaption={props.isWelcome || !isLicenseConsumptionFeatureEnabled}
                            consumedLicenseCount={props.consumedLicenseCount}
                        />
                    </FloatingPanelFooter>
                )}
                {isMaximized && (
                    <ConversationManagement
                        sessions={props.sessions}
                        disabled={props.isLoading || props.isStreaming || props.isLoadingHistory}
                        activeSessionId={activeSessionId}
                        isMaximized={true}
                        layout={props.layout}
                        onNewConversation={handleNewConversation}
                        onLoadConversation={handleLoadConversation}
                        onSessionDeleted={props.onSessionDeleted}
                        onSessionRenamed={props.onSessionRenamed}
                    />
                )}
                {props.feedbackModal}
            </FloatingPanelBody>
        </FloatingPanel>
    );
};

const PageLayout = (props: AIPanelLayoutProps) => {
    const isRestructuredNavPanelActive = useFeature(FLAG_RESTRUCTURED_NAVIGATION);
    const isLicenseConsumptionFeatureEnabled = Boolean(useFeature(FLAG_AI_LICENSE_CONSUMPTION));
    const { activeSessionId } = useAIPanelContext();
    return (
        <Box
            className={classNames(style.aiPanel, style.pageLayout, {
                twoColumnLayout: props.layout !== "subMenu",
            })}
            testId={testIds.workArea.aiPanel.pageLayoutContainer}
        >
            <Box
                className={classNames(style.body, {
                    [style.centeredContent]: props.isWelcome || props.isLoadingHistory,
                })}
            >
                {props.isLoadingHistory ? (
                    <Box data-testid={testIds.workArea.aiPanel.historyLoadingIndicator}>
                        <LogoLoader />
                    </Box>
                ) : props.isShowingOnboardingPrompt ? (
                    <AIPanelOnboardingWelcome
                        firstName={props.firstName}
                        isDarkMode={props.isDarkMode}
                        products={props.products}
                        isProductsLoading={props.isProductsLoading}
                        onOnboardingStateChange={props.onOnboardingStateChange}
                        onAccept={props.onOnboardingAccept}
                        onDecline={props.onOnboardingDecline}
                    />
                ) : props.isWelcome ? (
                    <AIPanelWelcome
                        showInlineInput
                        onSubmit={props.onSubmit}
                        isSubmitting={props.isLoading}
                        disabled={props.isLoadingHistory}
                        placeholder={props.queryPlaceholder}
                        isPageLayout
                        fullWidthOnDesktop
                    />
                ) : (
                    <AIPanelMessages
                        messages={props.messages}
                        isLoading={props.isLoading}
                        isStreaming={props.isStreaming}
                        toolStatus={props.toolStatus}
                        suggestions={props.suggestions}
                        visibleSuggestionCount={props.visibleSuggestionCount}
                        feedbackAnswer={props.feedbackAnswer}
                        isDarkMode={props.isDarkMode}
                        onSuggestionClick={props.onSuggestionClick}
                        onFeedbackClick={props.onFeedbackClick}
                        messagesEndRef={props.messagesEndRef}
                        attachMessagesEndRef={props.attachMessagesEndRef}
                        products={props.products}
                        isProductsLoading={props.isProductsLoading}
                        onOnboardingStateChange={props.onOnboardingStateChange}
                    />
                )}
            </Box>
            {!props.isWelcome && (
                <Box className={style.footer}>
                    <AIQueryInput
                        onSubmit={props.onSubmit}
                        disabled={props.isLoadingHistory}
                        isSubmitting={props.isLoading || props.isStreaming}
                        placeholder={props.queryPlaceholder}
                        queryInputTestId={testIds.workArea.aiPanel.queryInput}
                        submitButtonTestId={testIds.workArea.aiPanel.submitButton}
                        hideLicenseConsumptionCaption={!isLicenseConsumptionFeatureEnabled}
                        consumedLicenseCount={props.consumedLicenseCount}
                    />
                </Box>
            )}
            {!isRestructuredNavPanelActive && (
                <ConversationManagement
                    sessions={props.sessions}
                    disabled={props.isLoading || props.isStreaming || props.isLoadingHistory}
                    activeSessionId={activeSessionId}
                    isMaximized={true}
                    layout={props.layout}
                    onNewConversation={props.onNewConversation}
                    onLoadConversation={props.onLoadConversation}
                    onSessionDeleted={props.onSessionDeleted}
                    onSessionRenamed={props.onSessionRenamed}
                />
            )}
            {props.feedbackModal}
        </Box>
    );
};

export const AIPanel = (props: Props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const {
        currentViewTitle,
        sectionTitle,
        isAiPanelOpen,
        workflowEditorTabs,
        closeWorkflowEditor,
        aiToolsApi,
        pendingPrompt,
        setPendingPrompt,
        setIsProcessingPrompt,
        sessions,
        setSessions,
        activeSessionId,
        setActiveSessionId,
        registerConversationManagementHandlers,
    } = useAIPanelContext();
    const isPanelHidden =
        aiToolsApi === undefined && props.layout === "floating" && location.pathname === HOME_ROUTE.path;
    const dispatch = useDispatch();
    const isAiOnboardingEnabled = useFeature(FLAG_AI_ONBOARDING);
    const currentUser = useSelector((state: StoreState) => state.userReducer.user);
    const hasAiUserProfile = isStringNotBlank(currentUser?.aiUserProfile);
    const firstName = getFirstName(currentUser?.name) ?? currentUser?.username;
    const [messages, setMessages] = React.useState<Message[]>([]);
    const [suggestions, setSuggestions] = React.useState<string[]>([]);
    const [consumedLicenseCount, setConsumedLicenseCount] = React.useState(0);
    const [sessionId, setSessionId] = React.useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [toolStatus, setToolStatus] = React.useState<string | undefined>(undefined);
    const [isLoadingHistory, setIsLoadingHistory] = React.useState(false);
    const [visibleSuggestionCount, setVisibleSuggestionCount] = React.useState(0);
    const [feedbackModalType, setFeedbackModalType] = React.useState<UserFeedback | undefined>(undefined);
    const [feedbackAnswer, setFeedbackAnswer] = React.useState<UserFeedback | undefined>(undefined);
    const [hasDeclinedOnboarding, setHasDeclinedOnboarding] = React.useState(false);
    const [isOnboardingInProgress, setIsOnboardingInProgress] = React.useState(false);

    const { current: automaticMessageCountRef } = React.useRef({ count: 0 });
    const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
    const { current: attachMessagesEndRef } = React.useRef((node: HTMLDivElement | null) => {
        messagesEndRef.current = node;
        node?.scrollIntoView({ behavior: "instant" });
    });
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const isDarkMode = useIsDarkThemeActive();
    const isFeedbackModalOpen = feedbackModalType !== undefined;

    React.useEffect(() => {
        return () => abortControllers.forEach((abortController) => abortController.abort());
    }, []);

    const abortOutstandingRequests = () => {
        abortControllers.forEach((abortController) => abortController.abort());
        abortControllers.length = 0;
    };

    const conversationHasProductTag = messages.some(
        (message) => message.role === "assistant" && extractProductTags(message.content).length > 0
    );
    const {
        products,
        isLoading: isProductsLoading,
        onOnboardingStateChange,
    } = useFilteredProducts(conversationHasProductTag);

    React.useEffect(() => {
        if (pendingPrompt) {
            submitMessage(pendingPrompt);
            setPendingPrompt(undefined);
        }
    }, [pendingPrompt]);

    React.useEffect(() => {
        if (activeSessionId === sessionId) {
            return;
        }
        if (activeSessionId === undefined) {
            setMessages([]);
            setSessionId(undefined);
            setFeedbackAnswer(undefined);
        } else {
            void handleLoadConversation(activeSessionId);
        }
    }, [activeSessionId]);

    React.useEffect(() => {
        if (sessionId !== undefined) {
            setHasDeclinedOnboarding(false);
        }
    }, [sessionId]);

    React.useEffect(() => {
        if (hasAiUserProfile) {
            setIsOnboardingInProgress(false);
        }
    }, [hasAiUserProfile]);

    const wasPanelHiddenRef = React.useRef(isPanelHidden);
    React.useEffect(() => {
        const becameUnhidden = !isPanelHidden && wasPanelHiddenRef.current;
        wasPanelHiddenRef.current = isPanelHidden;
        if (becameUnhidden && isAiPanelOpen && activeSessionId !== undefined && !isLoading && !isStreaming) {
            void handleLoadConversation(activeSessionId);
        }
    }, [isPanelHidden, isAiPanelOpen, activeSessionId, isLoading, isStreaming]);

    React.useEffect(() => {
        const shouldRefetch =
            props.layout === "floating" &&
            isAiPanelOpen &&
            !isPanelHidden &&
            activeSessionId !== undefined &&
            !isLoading &&
            !isStreaming;
        if (shouldRefetch) {
            void handleLoadConversation(activeSessionId);
        }
    }, [isAiPanelOpen]);

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    React.useEffect(() => {
        if (visibleSuggestionCount === 0 || visibleSuggestionCount >= suggestions.length) {
            return;
        }
        const timeout = setTimeout(() => {
            setVisibleSuggestionCount((prev) => prev + 1);
        }, SUGGESTION_REVEAL_INTERVAL_MILLISECONDS);
        return () => clearTimeout(timeout);
    }, [visibleSuggestionCount, suggestions.length]);

    React.useEffect(() => {
        if (visibleSuggestionCount > 0) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [visibleSuggestionCount]);

    const submitMessage = async (
        trimmedQuery: string,
        isAutomatic: boolean = false,
        sessionIdOverride?: string,
        onboarding: boolean = false
    ) => {
        if (!isAutomatic) {
            automaticMessageCountRef.count = 0;
        }
        abortOutstandingRequests();
        setSuggestions([]);
        setFeedbackAnswer(undefined);
        setVisibleSuggestionCount(0);
        if (!isAutomatic) {
            setMessages((prev) => [...prev, { role: "user", content: trimmedQuery }]);
        }
        setIsLoading(true);
        setToolStatus(undefined);
        setIsProcessingPrompt(true);
        setAiEditModeIfTabOpen(aiToolsApi, true);

        usageStatisticsService.sendEvent({
            category: Category.AI_AGENT,
            action: Action.SEND_MESSAGE,
        });

        const currentView =
            sectionTitle && currentViewTitle && sectionTitle !== currentViewTitle
                ? `${sectionTitle} - ${currentViewTitle}`
                : (sectionTitle ?? currentViewTitle ?? "");

        const effectiveSessionId = sessionIdOverride ?? sessionId;
        const isNewConversation = effectiveSessionId === undefined;
        const isOnboarding = onboarding || isOnboardingInProgress;
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const currentTab = aiToolsApi ? await getCurrentTabIfOpen(aiToolsApi) : undefined;
        const allTabs = aiToolsApi ? aiToolsApi.getTabs() : undefined;
        const currentWorkflowName = getWorkflowName(currentTab?.workflow);
        const currentWorkflow = currentTab ? JSON.stringify(currentTab.workflow) : undefined;
        const currentWorkflowProfile = currentTab?.product;
        const currentErrors = currentTab?.errors;
        try {
            let accumulated = "";
            let replyText = "";
            let followUpPrompts: string[] = [];
            let firstToken = true;
            for await (const event of aiAgentService.sendMessageStream(
                trimmedQuery,
                currentView,
                LanguageRepository.getLanguage().code,
                getLocalTimezone(),
                currentWorkflowName,
                currentWorkflow,
                currentWorkflowProfile,
                currentErrors,
                allTabs,
                isAutomatic,
                isOnboarding,
                effectiveSessionId,
                abortController
            )) {
                if (abortController.signal.aborted) {
                    break;
                }
                if (event.type === "token") {
                    accumulated = transformContent((accumulated += event.token));
                    const split = splitFollowUpPrompts(accumulated);
                    replyText = split.text;
                    followUpPrompts = split.prompts;
                    const displayContent = streamTagContent(replyText);
                    if (firstToken) {
                        firstToken = false;
                        setIsLoading(false);
                        setIsStreaming(true);
                        setMessages((prev) => [...prev, { role: "assistant", content: displayContent }]);
                    } else {
                        setMessages((prev) => {
                            const msgs = [...prev];
                            msgs[msgs.length - 1] = {
                                role: "assistant",
                                content: displayContent,
                            };
                            return msgs;
                        });
                    }
                } else if (event.type === "toolStatus") {
                    setToolStatus(getToolStatus(event.toolName, t));
                } else if (event.type === "error") {
                    // A turn can fail after doing billable work, and no "done" event follows an
                    // error, so take the session and consumed count from the error itself.
                    if (event.consumedLicenseTokens !== undefined) {
                        setConsumedLicenseCount(event.consumedLicenseTokens);
                    }
                    if (event.sessionId !== undefined) {
                        setSessionId(event.sessionId);
                        setActiveSessionId(event.sessionId);
                    }
                    setMessages((prev) => [
                        ...prev,
                        { role: "assistant", content: t(getErrorTranslationKey(event.code)) },
                    ]);
                    break;
                } else if (event.type === "done") {
                    setConsumedLicenseCount(event.consumedLicenseTokens);
                    setSessionId(event.sessionId);
                    setActiveSessionId(event.sessionId);
                    setSuggestions(buildSuggestions(replyText, followUpPrompts));
                    setVisibleSuggestionCount(1);
                    if (isOnboarding) {
                        userSessionService
                            .fetchUserDetails()
                            .then((updatedUser) => dispatch(setUser(updatedUser)))
                            .catch(() => {
                                // Profile refresh will be retried after the next onboarding message.
                            });
                    }
                    if (isNewConversation) {
                        aiAgentService
                            .fetchSessions(abortController)
                            .then(setSessions)
                            .catch(() => {
                                // Keep whatever sessions are already in state.
                            });
                    }

                    if (aiToolsApi) {
                        const updatedErrors = await applyWorkflowBlocks(aiToolsApi, accumulated);
                        const autoFixableErrors = getAutoFixableErrors(updatedErrors);
                        if (autoFixableErrors.length === 0) {
                            automaticMessageCountRef.count = 0;
                        } else if (automaticMessageCountRef.count < MAX_AUTOMATIC_MESSAGES) {
                            automaticMessageCountRef.count += 1;
                            void submitMessage("", true, event.sessionId);
                        }
                    }
                }
            }
        } catch {
            if (!abortController.signal.aborted) {
                setMessages((prev) => [...prev, { role: "assistant", content: t("AiAgent.errorResponse") }]);
            }
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoading(false);
                setIsStreaming(false);
                setIsProcessingPrompt(false);
                setAiEditModeIfTabOpen(aiToolsApi, false);
            }
        }
    };

    const buildSuggestions = (replyText: string, followUpPrompts: string[]): string[] => {
        if (replyText.trim() === "") {
            return [];
        }
        return replyText.includes(`\`summary\``)
            ? [t("AiAgent.supportCard.supportFollowUpPrompt"), ...followUpPrompts]
            : followUpPrompts;
    };

    const handleSuggestionClick = (suggestion: string) => {
        if (isLoading || isLoadingHistory) {
            return;
        }
        void submitMessage(suggestion);
    };

    const handleOnboardingAccept = () => {
        if (isLoading || isLoadingHistory) {
            return;
        }
        setIsOnboardingInProgress(true);
        void submitMessage(t("Common.yes"), false, undefined, true);
    };

    const handleOnboardingDecline = () => {
        if (isLoading || isLoadingHistory) {
            return;
        }
        setHasDeclinedOnboarding(true);
        handleNewConversation();
    };

    const handleLoadConversation = async (loadSessionId: string) => {
        abortOutstandingRequests();
        setIsLoading(false);
        setIsStreaming(false);
        setIsLoadingHistory(true);
        setIsOnboardingInProgress(false);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        try {
            const result = await aiAgentService.fetchSessionById(loadSessionId, abortController);
            setSessionId(result.sessionId);
            setActiveSessionId(result.sessionId);
            setConsumedLicenseCount(result.licensesConsumed);
            const historyMessages = concatAssistantMessages(result.messages);
            const lastMessage = historyMessages[historyMessages.length - 1];
            const lastReply =
                lastMessage?.role === "assistant"
                    ? splitFollowUpPrompts(transformContent(lastMessage.content))
                    : undefined;
            const historySuggestions = lastReply ? buildSuggestions(lastReply.text, lastReply.prompts) : [];
            setMessages(
                historyMessages.map((item) => ({
                    ...item,
                    content:
                        item.role === "assistant"
                            ? splitFollowUpPrompts(transformContent(item.content)).text
                            : item.content,
                }))
            );
            setSuggestions(historySuggestions);
            setVisibleSuggestionCount(historySuggestions.length);
        } catch {
            if (!abortController.signal.aborted) {
                setMessages([]);
                setSessionId(undefined);
                setActiveSessionId(undefined);
                setConsumedLicenseCount(0);
            }
        } finally {
            if (!abortController.signal.aborted) {
                setFeedbackAnswer(undefined);
                setIsLoadingHistory(false);
            }
        }
    };

    // TODO BCC-6700: Refactor states.
    const handleNewConversation = () => {
        abortOutstandingRequests();
        setIsLoading(false);
        setIsStreaming(false);
        setIsLoadingHistory(false);
        setMessages([]);
        setSessionId(undefined);
        setActiveSessionId(undefined);
        setConsumedLicenseCount(0);
        setSuggestions([]);
        setVisibleSuggestionCount(0);
        setFeedbackAnswer(undefined);
        setIsProcessingPrompt(false);
    };

    const handleSessionDeleted = (deletedSessionId: string) => {
        setSessions(sessions.filter((session) => session.sessionId !== deletedSessionId));
        if (sessionId === deletedSessionId) {
            handleNewConversation();
        }
    };

    const handleSessionRenamed = (renamedSessionId: string, name: string) => {
        setSessions(
            sessions.map((session) => (session.sessionId === renamedSessionId ? { ...session, name } : session))
        );
    };

    const handleNewConversationRef = React.useRef(handleNewConversation);
    handleNewConversationRef.current = handleNewConversation;
    const handleLoadConversationRef = React.useRef(handleLoadConversation);
    handleLoadConversationRef.current = handleLoadConversation;
    const handleSessionDeletedRef = React.useRef(handleSessionDeleted);
    handleSessionDeletedRef.current = handleSessionDeleted;
    const handleSessionRenamedRef = React.useRef(handleSessionRenamed);
    handleSessionRenamedRef.current = handleSessionRenamed;

    const { current: conversationManagementCallbacks } = React.useRef({
        onNewConversation: () => handleNewConversationRef.current(),
        onLoadConversation: (loadSessionId: string) => handleLoadConversationRef.current(loadSessionId),
        onSessionDeleted: (deletedSessionId: string) => handleSessionDeletedRef.current(deletedSessionId),
        onSessionRenamed: (renamedSessionId: string, name: string) =>
            handleSessionRenamedRef.current(renamedSessionId, name),
    });

    React.useEffect(() => {
        if (props.layout !== "subMenu") {
            return undefined;
        }
        const handlers: ConversationManagementHandlers = {
            disabled: isLoading || isStreaming || isLoadingHistory,
            ...conversationManagementCallbacks,
        };
        registerConversationManagementHandlers(handlers);
        return () => registerConversationManagementHandlers(undefined);
    }, [
        props.layout,
        isLoading,
        isStreaming,
        isLoadingHistory,
        registerConversationManagementHandlers,
        conversationManagementCallbacks,
    ]);

    const handleFeedbackButtonClick = (rating: UserFeedback) => {
        if (feedbackAnswer) {
            setFeedbackAnswer(undefined);
            return;
        }
        setFeedbackModalType(rating);
    };

    const handleFeedbackSubmit = (rating: UserFeedback, reason?: string, details?: string) => {
        setFeedbackAnswer(rating);
        setFeedbackModalType(undefined);
        if (sessionId === undefined) {
            return;
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        aiAgentService
            .postFeedback(sessionId, rating, reason, details, abortController)
            .then(() => showToast(t("AiAgent.feedback.submitted"), "success"))
            .catch(() => {
                // Fail silently. User should not be shown error if the feedback submit fails.
            });
    };

    const isWelcome = messages.length === 0 && !isLoading;
    const isShowingOnboardingPrompt =
        isAiOnboardingEnabled && isWelcome && sessionId === undefined && !hasAiUserProfile && !hasDeclinedOnboarding;

    const feedbackModal = (
        <AIFeedbackModal
            feedback={feedbackModalType ?? "negative"}
            isOpen={isFeedbackModalOpen}
            hideModal={() => setFeedbackModalType(undefined)}
            onSubmit={(reason, details) => handleFeedbackSubmit(feedbackModalType ?? "negative", reason, details)}
        />
    );

    const layoutProps: AIPanelLayoutProps = {
        layout: props.layout,
        sessions,
        messages,
        suggestions,
        visibleSuggestionCount,
        isLoading,
        isStreaming,
        toolStatus,
        isLoadingHistory,
        feedbackAnswer,
        isWelcome,
        isDarkMode,
        messagesEndRef,
        attachMessagesEndRef,
        queryPlaceholder: t("AiAgent.promptPlaceholder"),
        onSubmit: submitMessage,
        onSuggestionClick: handleSuggestionClick,
        onFeedbackClick: handleFeedbackButtonClick,
        onNewConversation: handleNewConversation,
        onLoadConversation: handleLoadConversation,
        onSessionDeleted: handleSessionDeleted,
        onSessionRenamed: handleSessionRenamed,
        feedbackModal,
        products,
        isProductsLoading,
        onOnboardingStateChange,
        isShowingOnboardingPrompt,
        firstName,
        onOnboardingAccept: handleOnboardingAccept,
        onOnboardingDecline: handleOnboardingDecline,
        consumedLicenseCount,
    };

    if (props.layout === "floating") {
        return (
            <>
                <div className={isPanelHidden ? style.hiddenPanel : undefined}>
                    <FloatingLayout {...layoutProps} />
                </div>
                <WorkflowEditorV3View
                    isOpen={workflowEditorTabs !== undefined}
                    onClose={closeWorkflowEditor}
                    initialTabs={workflowEditorTabs}
                    entityType={WorkflowEntityType.WORKFLOW}
                />
            </>
        );
    }

    return <PageLayout {...layoutProps} />;
};
